import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Dialog, DialogContent, DialogActions, Grid } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useToast } from 'core/components/toast';
import { setTouchedFromErrors } from 'core/components/validation-errors-modal/validation-errors-modal';
import { useStore } from 'core/store/store.mobx';
import useSession from 'core/hooks/use-session';
import ActivePolicyAckModal from './active-policy-ack-modal';
import viewTask from 'core/assets/svg/view-task-white.svg';
import documentApprovalIcon from 'core/assets/svg/document-approval-outlined.svg';
import shareIcon from 'core/assets/svg/ic-share.svg';
import FarmOfferSharingOptions from 'offer/components/farm-offer-sharing-options';
import useStyles from '../footer.styles';

// CFM auto UW review + approval footer. A direct clone of FarmOfferFooter (#32744) so CFM auto has
// the identical "send to underwriting → underwriter approves → checkout unlocked" workflow. The
// send-to-underwriting and approve-for-checkout store actions/mutations are generic (they act on
// preBindUWCheck.approvalDetails, not on any farm field), so they're reused as-is; only the routing
// (verifyAutoUW) and copy differ. The underwriter view also reuses the farm "Offer sharing options"
// modal (send to acting agent / copy link / PDFs): its quote PDF goes through offerStore.downloadOffer,
// which routes standalone CFM auto to the DocGen quote and the auto bundles (AB/AR) to the legacy
// DocRaptor path (usesDocGenQuotePdf, #35921 / #36206), so it is safe for every auto option.

const AutoUnderwritingReviewPageFooter = observer(({ goToUWForm }) => {
  const { offer: store } = useStore();
  const classes = useStyles();
  const { canEditOffers } = useSession();

  return (
    <>
      <div>
        <Label className={classes.title} type="subtitle">
          We'll ask a few quick underwriting questions
        </Label>
        <Label className={classes.subtitle} type="subtitle">
          This helps confirm the auto policy meets eligibility requirements. It only takes a few seconds.
        </Label>
      </div>
      <Grid container justifyContent="center" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          className={classes.wideBtn}
          disabled={store.loading || !canEditOffers}
          onClick={goToUWForm}
        >
          <Label className={classes.btnLabel}>Continue to Underwriting Review</Label>
        </Button>
      </Grid>
    </>
  );
});

const SendToUnderwritingConfirmModal = ({ open, onClose, onConfirm }) => {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogContent className={classes.modalContainer}>
        <Label type="greenBig">Are you sure you want to send this for review?</Label>
        <Label type="modalBody">
          This will lock the offer, and you won't be able to make changes until underwriting has reviewed it.
        </Label>
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <Button variant="contained" color="secondary" className={classes.btn} onClick={onClose}>
          <Label className={classes.btnLabel}>No, cancel</Label>
        </Button>
        <Button variant="contained" color="primary" className={classes.btn} onClick={onConfirm}>
          <Label className={classes.btnLabel}>Yes, send to underwriting</Label>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AutoOfferFooter = observer(({ onContinueToCheckout, setShowValidationErrorsModal }) => {
  const { offer: store } = useStore();
  const classes = useStyles();
  const toast = useToast();
  const { canEditOffers, canApproveUWReview, user } = useSession();
  const username = user?.username;
  const { validateForm, setErrors, setTouched } = useFormikContext();
  const history = useHistory();
  const [showSendToUWModal, setShowSendToUWModal] = useState(false);
  const [sharingModalOpen, setSharingModalOpen] = useState(false);
  const [activePolicyAck, setActivePolicyAck] = useState({ open: false, ids: [] });

  const approvalDetails = store.offer?.quote?.preBindUWCheck?.approvalDetails;
  const sentToUnderwritingAt = approvalDetails?.sentToUnderwritingAt;
  const underwriterApprovedAt = approvalDetails?.underwriterApprovedAt;
  const underwriterApprovedBy = approvalDetails?.underwriterApprovedBy;
  const isApproved = underwriterApprovedAt && underwriterApprovedBy;
  // #35936: a policy already exists for this quote (fetchBoundPolicy). EXACT = bound from THIS offer
  // id → hard-block Send-to-UW (re-sending would duplicate it). FUZZY = an active policy for this
  // member at this location / for these cars (a different or unknown quote) → the member may
  // legitimately hold several policies, so we warn and let the agent send anyway (mirrors #35249).
  const boundPolicy = store.boundPolicy;
  const boundPolicyIsExact = store.boundPolicyIsExact;

  const goToUWForm = useCallback(async () => {
    const updateErrors = await validateForm();
    if (Object.keys(updateErrors).length > 0) {
      setErrors(updateErrors);
      setTouched(setTouchedFromErrors(updateErrors), false);
      setShowValidationErrorsModal(true);
    } else {
      history.push(`/offer/${store.offer.id}/verifyAutoUW`);
    }
  }, [history.push, setShowValidationErrorsModal, validateForm, setErrors, setTouched, store.offer.id]);

  const autoOnSendToUW = useCallback(
    async ({ acknowledgeExistingActivePolicy = false } = {}) => {
      const validationErrors = await validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setTouched(setTouchedFromErrors(validationErrors), false);
        setShowValidationErrorsModal(true);
        return;
      }

      const sentToUnderwritingAt = new Date().toISOString();
      const sentToUnderwritingBy = username;

      const result = await store.sendOfferForUnderwritingApproval(
        store.offer.id,
        sentToUnderwritingAt,
        sentToUnderwritingBy,
        history,
        { acknowledgeExistingActivePolicy }
      );

      // #35936: fuzzy active-policy match — confirm "send anyway?" then retry with the ack flag.
      if (result?.needsActivePolicyAck) {
        setActivePolicyAck({ open: true, ids: result.activePolicyIds ?? [] });
        return;
      }

      // The store flow returns { ok: false } when the mutation throws — its catch already surfaced
      // the error dialog. Guard with optional chaining so we don't TypeError here.
      if (result?.ok) {
        toast.notify({
          type: 'success',
          message:
            "Your offer is on its way to our underwriting team. You'll hear back within one business day—we'll keep you posted along the way."
        });
      }
    },
    [validateForm, setErrors, setTouched, setShowValidationErrorsModal, username, store, toast, history]
  );

  const autoUWApproveForCheckout = useCallback(async () => {
    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched(setTouchedFromErrors(validationErrors), false);
      setShowValidationErrorsModal(true);
      return;
    }

    const uwApprovalDetails = {
      underwriterApprovedAt: new Date().toISOString(),
      underwriterApprovedBy: username
    };

    await store.uwApproveForCheckout(store.offer.id, uwApprovalDetails, history);

    if (!store.errors?.length) {
      toast.notify({
        type: 'success',
        message: "You've approved this offer for checkout."
      });
    }
  }, [validateForm, setErrors, setTouched, setShowValidationErrorsModal, username, store, history, toast]);

  if (canApproveUWReview) {
    return (
      <>
        <div>
          <Label className={classes.title} type="subtitle">
            Your offer is ready
          </Label>
          <div className={classes.copyLinksContainer}>
            <Label type="infoValue">Want to share this offer?</Label>
            <Button
              color="secondary"
              variant="text"
              className={classes.sharingOptionsButton}
              onClick={() => setSharingModalOpen(true)}
            >
              <img src={shareIcon} alt="" className={classes.shareIcon} /> Offer sharing options
            </Button>
          </div>
        </div>
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers}
              onClick={goToUWForm}
            >
              <img src={viewTask} alt="" className={classes.btnIcon} />
              <Label className={classes.btnLabel}>Review underwriting</Label>
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers || isApproved}
              onClick={autoUWApproveForCheckout}
            >
              <img src={documentApprovalIcon} alt="" className={classes.btnIcon} />
              <Label className={classes.btnLabel}>Approve for checkout</Label>
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="contained"
              color="primary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers || !isApproved}
              onClick={onContinueToCheckout}
            >
              <Label className={classes.btnLabel}>Continue to checkout</Label>
            </Button>
          </Grid>
        </Grid>
        <FarmOfferSharingOptions open={sharingModalOpen} onClose={() => setSharingModalOpen(false)} />
      </>
    );
  }

  // Default: user does not have underwriting approval permission
  if (store.needsPreBindUWForm) {
    return (
      <AutoUnderwritingReviewPageFooter
        setShowValidationErrorsModal={setShowValidationErrorsModal}
        goToUWForm={goToUWForm}
      />
    );
  }

  return (
    <>
      <div>
        <Label className={classes.title} type="subtitle">
          Your offer is ready
        </Label>
      </div>
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        {!sentToUnderwritingAt && (
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers || (Boolean(boundPolicy) && boundPolicyIsExact)}
              onClick={() => setShowSendToUWModal(true)}
            >
              <Label className={classes.btnLabel}>Send to underwriting</Label>
            </Button>
            {boundPolicy && boundPolicyIsExact && (
              <Label className={classes.sentToUnderwritingText} type="infoValue">
                {`Policy ${boundPolicy.id} was already purchased from this quote`}
              </Label>
            )}
          </Grid>
        )}

        <Grid item>
          <Button
            variant="contained"
            color="primary"
            className={classes.btn}
            disabled={store.loading || !canEditOffers || !isApproved}
            onClick={onContinueToCheckout}
          >
            <Label className={classes.btnLabel}>Continue to checkout</Label>
          </Button>
        </Grid>
      </Grid>

      <SendToUnderwritingConfirmModal
        open={showSendToUWModal}
        onClose={() => setShowSendToUWModal(false)}
        onConfirm={() => {
          setShowSendToUWModal(false);
          autoOnSendToUW();
        }}
      />

      <ActivePolicyAckModal
        open={activePolicyAck.open}
        policyIds={activePolicyAck.ids}
        onClose={() => setActivePolicyAck({ open: false, ids: [] })}
        onConfirm={() => {
          setActivePolicyAck({ open: false, ids: [] });
          autoOnSendToUW({ acknowledgeExistingActivePolicy: true });
        }}
      />
    </>
  );
});

AutoOfferFooter.propTypes = {
  onContinueToCheckout: PropTypes.func.isRequired,
  setShowValidationErrorsModal: PropTypes.func.isRequired
};

export default AutoOfferFooter;
